import * as React from 'react';
import { Command, AlertCircle, RefreshCw, ShieldAlert, Wifi, WifiOff } from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';
import { useFocusTrap } from '../lib/use-focus-trap';

/**
 * Canonical operational console contract.
 *
 * Every operational console (SCADA op, alarms console, batch executor, etc)
 * should compose `ConsoleShell`. Bundles the 6 required affordances:
 *
 *   1. Keyboard-accessible command palette (Ctrl/⌘+K opens; arrow + enter)
 *   2. Role-aware disabled actions with reason (`commands[i].deniedReason`)
 *   3. Live status (`liveStatus.connected` + label)
 *   4. Stale data indicator (`staleSince` ISO; > `staleAfterMs` flips to stale)
 *   5. Retry (`onRetry` — wired into stale banner + connection lost banner)
 *   6. Safe command confirmation (commands marked `requiresConfirm` open inline modal)
 *
 * Only the palette is unconditional. Affordances 2-6 appear only when the view
 * passes the corresponding prop or command member, which is exactly how
 * `scripts/audit-operational-console-archetype-coverage.mjs` scores them: from
 * the AST of the call site, not from the presence of the import.
 */

export interface ConsoleCommand {
  /** Stable id; also key in palette. */
  id: string;
  /** Visible label. */
  label: string;
  /** Optional descriptive helper text. */
  hint?: string;
  /** Action handler. Resolves once the command has been executed. */
  run: () => void | Promise<void>;
  /** Operator role gates. When `deniedReason` is set, the command is shown as disabled. */
  deniedReason?: string;
  /** Confirm modal before running (irreversible / cross-process effects). */
  requiresConfirm?: boolean;
  /** Optional confirm prompt copy. */
  confirmPrompt?: string;
  /** Visual variant. */
  variant?: 'default' | 'destructive';
  /** Optional shortcut key shown in palette ("g" or "shift+r"). */
  shortcut?: string;
}

export interface ConsoleLiveStatus {
  connected: boolean;
  /** Optional tag/source label. */
  label?: string;
}

export interface ConsoleShellProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Main console body (live tag grid, alarm list, batch phase, etc). */
  children: React.ReactNode;
  /** Available commands (palette + visible toolbar buttons). */
  commands?: ConsoleCommand[];
  /** Live status indicator. */
  liveStatus?: ConsoleLiveStatus;
  /** ISO timestamp of last successful data refresh. */
  staleSince?: string | null;
  /** ms beyond which `staleSince` is considered stale. Default 30000. */
  staleAfterMs?: number;
  /** Retry handler — invoked from stale + offline banners. */
  onRetry?: () => void;
  className?: string;
}

export function ConsoleShell({
  title,
  subtitle,
  children,
  commands = [],
  liveStatus,
  staleSince,
  staleAfterMs = 30_000,
  onRetry,
  className,
}: ConsoleShellProps) {
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [paletteFilter, setPaletteFilter] = React.useState('');
  const [paletteIdx, setPaletteIdx] = React.useState(0);
  const [confirm, setConfirm] = React.useState<ConsoleCommand | null>(null);
  const [now, setNow] = React.useState<number>(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        setPaletteFilter('');
        setPaletteIdx(0);
      } else if (e.key === 'Escape') {
        setPaletteOpen(false);
        setConfirm(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filteredCommands = React.useMemo(() => {
    if (!paletteFilter) return commands;
    const q = paletteFilter.toLowerCase();
    return commands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q)
    );
  }, [commands, paletteFilter]);

  React.useEffect(() => {
    setPaletteIdx(0);
  }, [paletteFilter]);

  function handleCommand(cmd: ConsoleCommand) {
    if (cmd.deniedReason) return;
    setPaletteOpen(false);
    if (cmd.requiresConfirm) {
      setConfirm(cmd);
    } else {
      void cmd.run();
    }
  }

  function handlePaletteKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPaletteIdx((i) => Math.min(filteredCommands.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPaletteIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filteredCommands[paletteIdx];
      if (cmd) handleCommand(cmd);
    }
  }

  const isStale =
    staleSince != null && now - new Date(staleSince).getTime() > staleAfterMs;
  const isOffline = liveStatus?.connected === false;

  // Focus traps for the two hand-rolled overlays — palette + confirm modal.
  // Restores focus to the trigger on close (WCAG 2.4.3 — Focus Order).
  const paletteTrapRef = useFocusTrap<HTMLDivElement>({
    active: paletteOpen,
    onEscape: () => setPaletteOpen(false),
  });
  const confirmTrapRef = useFocusTrap<HTMLDivElement>({
    active: confirm != null,
    onEscape: () => setConfirm(null),
  });

  return (
    <div className={cn('flex flex-col gap-3 p-4', className)} data-testid="console-shell">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{title}</h1>
          {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {liveStatus ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ring-inset',
                liveStatus.connected
                  ? 'bg-[hsl(var(--state-running))]/10 text-[hsl(var(--state-running))] ring-[hsl(var(--state-running))]/30'
                  : 'bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))] ring-[hsl(var(--alarm-critical))]/40'
              )}
              data-testid="console-live-status"
            >
              {liveStatus.connected ? (
                <Wifi className="h-3 w-3" aria-hidden />
              ) : (
                <WifiOff className="h-3 w-3" aria-hidden />
              )}
              {liveStatus.label ?? (liveStatus.connected ? 'Live' : 'Disconnected')}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPaletteOpen(true);
              setPaletteFilter('');
              setPaletteIdx(0);
            }}
            data-testid="console-palette-trigger"
            aria-label="Open command palette"
          >
            <Command className="mr-1 h-4 w-4" aria-hidden /> ⌘K
          </Button>
        </div>
      </header>

      {(isStale || isOffline) && (
        <div
          className={cn(
            'flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm',
            isOffline
              ? 'border-[hsl(var(--alarm-critical))]/40 bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))]'
              : 'border-[hsl(var(--alarm-minor))]/40 bg-[hsl(var(--alarm-minor))]/10 text-[hsl(var(--alarm-minor))]'
          )}
          role="status"
          aria-live="polite"
          data-testid={isOffline ? 'console-offline-banner' : 'console-stale-banner'}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" aria-hidden />
            <span>
              {isOffline
                ? 'Connection to live source lost.'
                : `Data is stale (last update ${staleSince ? new Date(staleSince).toLocaleTimeString() : 'unknown'}).`}
            </span>
          </div>
          {onRetry ? (
            <Button size="sm" variant="ghost" onClick={onRetry} data-testid="console-retry">
              <RefreshCw className="mr-1 h-4 w-4" aria-hidden /> Retry
            </Button>
          ) : null}
        </div>
      )}

      {/* Toolbar — visible commands (those without `palette-only` hint). */}
      {commands.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-1 rounded-md border border-border/60 bg-surface px-2 py-1"
          role="toolbar"
          aria-label="Console commands"
        >
          {commands.slice(0, 6).map((c) => (
            <Button
              key={c.id}
              size="sm"
              variant={c.variant === 'destructive' ? 'destructive' : 'ghost'}
              onClick={() => handleCommand(c)}
              disabled={!!c.deniedReason}
              title={c.deniedReason ?? c.hint}
              data-testid={`console-cmd-${c.id}`}
            >
              {c.deniedReason ? <ShieldAlert className="mr-1 h-3 w-3" aria-hidden /> : null}
              {c.label}
            </Button>
          ))}
          {commands.length > 6 ? (
            <span className="text-xs text-muted-foreground">+{commands.length - 6} more in ⌘K</span>
          ) : null}
        </div>
      ) : null}

      <div className="flex-1">{children}</div>

      {paletteOpen ? (
        <div
          role="presentation"
          className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 p-12"
          data-testid="console-palette"
          onClick={(e) => {
            // Backdrop click only — clicks inside the dialog bubble up here too.
            if (e.target === e.currentTarget) setPaletteOpen(false);
          }}
        >
          <div
            ref={paletteTrapRef}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            className="w-full max-w-lg rounded-lg bg-background p-2 shadow-xl"
          >
            <input
              className="w-full rounded border border-border px-3 py-2 text-sm"
              placeholder="Type a command…"
              value={paletteFilter}
              onChange={(e) => setPaletteFilter(e.target.value)}
              onKeyDown={handlePaletteKey}
              role="combobox"
              aria-expanded
              aria-controls="console-palette-listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                filteredCommands[paletteIdx]
                  ? `console-palette-item-${filteredCommands[paletteIdx].id}`
                  : undefined
              }
              data-testid="console-palette-input"
            />
            <ul
              id="console-palette-listbox"
              className="mt-2 max-h-72 overflow-auto"
              role="listbox"
              aria-label="Commands"
            >
              {filteredCommands.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No matching commands</li>
              ) : (
                filteredCommands.map((c, i) => (
                  <li
                    key={c.id}
                    id={`console-palette-item-${c.id}`}
                    role="option"
                    aria-selected={i === paletteIdx}
                    onClick={() => handleCommand(c)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCommand(c);
                      }
                    }}
                    className={cn(
                      'flex cursor-pointer items-center justify-between rounded px-3 py-2 text-sm',
                      i === paletteIdx && 'bg-accent',
                      c.deniedReason && 'opacity-50'
                    )}
                    data-testid={`console-palette-item-${c.id}`}
                  >
                    <div>
                      <div className="font-medium">{c.label}</div>
                      {c.deniedReason ? (
                        <div className="text-xs text-[hsl(var(--alarm-critical))]">{c.deniedReason}</div>
                      ) : c.hint ? (
                        <div className="text-xs text-muted-foreground">{c.hint}</div>
                      ) : null}
                    </div>
                    {c.shortcut ? (
                      <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">{c.shortcut}</kbd>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      ) : null}

      {confirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label={`Confirm ${confirm.label}`}
          aria-labelledby="console-confirm-title"
          data-testid="console-confirm-modal"
        >
          <div
            ref={confirmTrapRef}
            className="w-full max-w-md rounded-lg bg-background p-4 shadow-lg"
          >
            <h2 id="console-confirm-title" className="text-lg font-semibold">
              Confirm: {confirm.label}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {confirm.confirmPrompt ?? 'This action cannot be undone. Continue?'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant={confirm.variant === 'destructive' ? 'destructive' : 'default'}
                size="sm"
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  void c.run();
                }}
                data-testid="console-confirm-run"
              >
                Run
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
